/**
 * Utility functions for graceful degradation of provider fields
 * Checks if fields/sections have meaningful data to display
 */

/**
 * Checks if a value is not empty (not null, undefined, or empty string)
 */
export const hasValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (typeof value === 'number') return true;
  if (typeof value === 'boolean') return true;
  return true;
};

/**
 * Checks if an array has at least one element
 */
export const hasArrayData = <T,>(arr: T[] | null | undefined): arr is T[] => {
  return Array.isArray(arr) && arr.length > 0;
};

/**
 * Checks if any of the provided values has data
 */
export const hasAnyValue = (...values: unknown[]): boolean => {
  return values.some(hasValue);
};

/**
 * Checks if provider has meaningful service range data
 */
export const hasServiceRangeData = (provider: { 
  serviceRanges?: string[] | null;
  serviceDeliveryTypes?: string | null;
}): boolean => {
  return hasArrayData(provider.serviceRanges) || hasValue(provider.serviceDeliveryTypes);
};

/**
 * Checks if Overview section has any data to display
 */
export const hasOverviewData = (provider: {
  companyOverview?: string | null;
  providerBio?: string | null;
  serviceOfferings?: string | null;
  professionalTitle?: string | null;
  certificationsArray?: string[] | null;
  specialties?: Array<{ id: string; name: string }> | null;
  registeredStates?: string[] | null;
  professionalLicenses?: string[] | null;
  languages?: string[] | null;
  professionalAssociations?: string[] | null;
  awardsRecognitions?: string[] | null;
  establishedYear?: number | null;
  yearsExperience?: number | null;
  serviceDeliveryTypes?: string | null;
  carePhilosophy?: string | null;
  whyThisWork?: string | null;
  emergencyResponseAvailable?: boolean | null;
  emergencyResponseDetails?: string | null;
  categories?: Array<{ id: string; name: string }> | null;
  expertise?: Array<{ id: string; name: string }> | null;
}): boolean => {
  return (
    hasValue(provider.companyOverview) ||
    hasValue(provider.providerBio) ||
    hasValue(provider.serviceOfferings) ||
    hasValue(provider.professionalTitle) ||
    hasArrayData(provider.certificationsArray) ||
    hasArrayData(provider.specialties) ||
    hasArrayData(provider.registeredStates) ||
    hasArrayData(provider.professionalLicenses) ||
    hasArrayData(provider.languages) ||
    hasArrayData(provider.professionalAssociations) ||
    hasArrayData(provider.awardsRecognitions) ||
    hasValue(provider.establishedYear) ||
    hasValue(provider.yearsExperience) ||
    hasValue(provider.serviceDeliveryTypes) ||
    hasValue(provider.carePhilosophy) ||
    hasValue(provider.whyThisWork) ||
    (provider.emergencyResponseAvailable && hasValue(provider.emergencyResponseDetails)) ||
    hasArrayData(provider.categories) ||
    hasArrayData(provider.expertise)
  );
};

/**
 * Checks if Services section has any data to display
 */
export const hasServicesData = (provider: {
  services?: Array<{ id: string }> | null;
}): boolean => {
  return hasArrayData(provider.services);
};

/**
 * Checks if Qualifications section has any data to display
 */
export const hasQualificationsData = (provider: {
  badges?: Array<{ badgeName: string }> | null;
  expertise?: Array<{ id: string }> | null;
  specialties?: Array<{ id: string }> | null;
  awardsRecognitions?: string[] | null;
  professionalAssociations?: string[] | null;
  certificationsArray?: string[] | null;
  professionalLicenses?: string[] | null;
}): boolean => {
  return (
    hasArrayData(provider.badges) ||
    hasArrayData(provider.expertise) ||
    hasArrayData(provider.specialties) ||
    hasArrayData(provider.awardsRecognitions) ||
    hasArrayData(provider.professionalAssociations) ||
    hasArrayData(provider.certificationsArray) ||
    hasArrayData(provider.professionalLicenses)
  );
};

/**
 * Checks if Media section has any data to display
 */
export const hasMediaData = (provider: {
  media?: Array<{ id: string }> | null;
}): boolean => {
  return hasArrayData(provider.media);
};

/**
 * Checks if Reviews section has any data to display
 */
export const hasReviewsData = (provider: {
  placeId?: string | null;
  googleReviews?: { reviews?: unknown[] } | null;
  averageRating?: number | null;
}): boolean => {
  // Has reviews if placeId exists (can potentially load reviews)
  // or if there's already rating/review data
  return (
    hasValue(provider.placeId) ||
    (hasValue(provider.averageRating) && (provider.averageRating || 0) > 0) ||
    hasArrayData(provider.googleReviews?.reviews)
  );
};

